import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Files, Clock3, CheckCircle2 } from 'lucide-react';
import { lettersApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatISTDate, formatISTDateTime, formatISTTime } from '../../utils/dateTime';

// Format ISO timestamp to dd-mm-yyyy hh:mm format
const formatDateTime = (dateString: string | null | undefined): string => {
  return formatISTDateTime(dateString);
};

interface LetterStats {
  total_letters: number;
  draft_letters: number;
  pending_approval_letters: number;
  approved_letters: number;
  rejected_letters: number;
  my_letters: number;
  my_pending_approvals: number;
  last_updated_at?: string | null;
  recentLetters?: Array<{
    id: number;
    letter_number: string;
    subject: string;
    file_name?: string;
    status: 'draft' | 'pending_approval' | 'approved' | 'rejected';
    letter_date: string;
    updated_at: string;
    created_by?: number;
    assigned_signatory_user_id?: number | null;
  }>;
}

export function LettersDashboard() {
  const { user } = useAuth();
  const [, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState<LetterStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingRoleByLetterId, setPendingRoleByLetterId] = useState<Record<number, 'pending_with_you' | 'requested_by_you'>>({});

  const load = useCallback(async ({ silent, signal }: { silent: boolean; signal?: AbortSignal }) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await lettersApi.getStats({ signal });
      setStats(response.data);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  // NOTE: no background polling. Stats are loaded once on mount (and after a
  // letter is created/approved via navigation). A 15s silent refetch made the
  // dashboard numbers flicker and kept the network tab busy for no benefit.
  useEffect(() => {
    load({ silent: false });
  }, [load]);

  useEffect(() => {
    let cancelled = false;

    const loadPendingRoleMap = async () => {
      const isAuthorizedSignatory = Boolean(user?.permissions?.is_authorized_signatory);
      if (!isAuthorizedSignatory) {
        setPendingRoleByLetterId({});
        return;
      }

      try {
        const [pendingWithYouRes, requestedByYouRes] = await Promise.all([
          lettersApi.listPendingApproval({ filter: 'pending_with_you' }),
          lettersApi.listPendingApproval({ filter: 'requested_by_you' }),
        ]);

        const pendingWithYou = Array.isArray(pendingWithYouRes?.data)
          ? pendingWithYouRes.data
          : (pendingWithYouRes?.data?.letters || []);
        const requestedByYou = Array.isArray(requestedByYouRes?.data)
          ? requestedByYouRes.data
          : (requestedByYouRes?.data?.letters || []);

        const nextMap: Record<number, 'pending_with_you' | 'requested_by_you'> = {};

        // First set pending_with_you, then override with requested_by_you.
        // This ensures creator-owned pending letters show as requested_by_you.
        pendingWithYou.forEach((letter: { id: number }) => {
          if (letter?.id) nextMap[Number(letter.id)] = 'pending_with_you';
        });
        requestedByYou.forEach((letter: { id: number }) => {
          if (letter?.id) nextMap[Number(letter.id)] = 'requested_by_you';
        });

        if (!cancelled) setPendingRoleByLetterId(nextMap);
      } catch {
        if (!cancelled) setPendingRoleByLetterId({});
      }
    };

    loadPendingRoleMap();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.permissions?.is_authorized_signatory]);

  const navigateTo = (page: string, extraParams: Record<string, string> = {}) => {
    const params = new URLSearchParams();
    params.set('page', page);
    Object.entries(extraParams).forEach(([key, value]) => params.set(key, value));
    setSearchParams(params);
  };

  const cards = [
    { title: 'Total Letters', value: stats?.total_letters || 0, icon: FileText, color: 'from-violet-500 to-fuchsia-600' },
    { title: 'Drafts', value: stats?.draft_letters || 0, icon: Clock3, color: 'from-amber-500 to-orange-600' },
    { title: 'Pending Approval', value: stats?.pending_approval_letters || 0, icon: Files, color: 'from-blue-500 to-indigo-600' },
    { title: 'Approved Letters', value: stats?.approved_letters || 0, icon: CheckCircle2, color: 'from-emerald-500 to-green-600' },
  ];

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Letter Generation Dashboard</h1>
          <p className="text-gray-600 mt-2">Create official letters, manage drafts, and track editing activity in one place.</p>
        </div>

      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {cards.map(({ title, value, icon: Icon, color }) => (
          <div key={title} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{title}</p>
                <p className="text-3xl font-bold text-gray-900 mt-3">{loading ? '—' : value}</p>
              </div>
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} text-white flex items-center justify-center`}>
                <Icon className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Recent Letters</h2>
            <button onClick={() => navigateTo('letters-view')} className="text-sm font-medium text-violet-700 hover:text-violet-800">See all</button>
          </div>

          <div className="space-y-3">
            {(stats?.recentLetters || []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-500">
                No letters yet. Create your first official letter.
              </div>
            ) : (
              stats?.recentLetters?.map((letter) => (
                <button
                  key={letter.id}
                  onClick={() => {
                    const pendingRole = pendingRoleByLetterId[Number(letter.id)];
                    const isPendingWithMe = pendingRole === 'pending_with_you';

                    if (letter.status === 'draft') {
                      navigateTo('letters-create', { letterId: String(letter.id) });
                    } else if (letter.status === 'approved') {
                      navigateTo('letters-view', { letterId: String(letter.id) });
                    } else {
                      // pending/rejected → open approval page in the correct tab for current user role
                      navigateTo('letters-pending-approval', {
                        openLetterId: String(letter.id),
                        filter: isPendingWithMe ? 'pending_with_you' : 'requested_by_you',
                      });
                    }
                  }}
                  className="w-full text-left rounded-xl border border-gray-200 hover:border-violet-300 hover:bg-violet-50 p-4 transition"
                >
                  {(() => {
                    const pendingRole = pendingRoleByLetterId[Number(letter.id)];
                    const isPendingWithMe = pendingRole === 'pending_with_you';
                    const isRequestedByMe = pendingRole === 'requested_by_you';

                    const statusLabel =
                      letter.status === 'pending_approval'
                        ? isPendingWithMe
                          ? 'Pending with You'
                          : isRequestedByMe
                            ? 'Waiting for Approval'
                            : 'Pending Approval'
                        : letter.status === 'approved'
                          ? 'Approved'
                          : letter.status === 'rejected'
                            ? 'Rejected'
                            : 'Draft';

                    return (
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="font-semibold text-gray-900">{letter.file_name || letter.subject}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        {letter.letter_number || 'Pending Approval'} · {formatISTDate(letter.letter_date)} · {formatISTTime(letter.updated_at)}
                      </div>
                    </div>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              letter.status === 'approved'
                                ? 'bg-emerald-100 text-emerald-700'
                                : letter.status === 'pending_approval'
                                  ? 'bg-blue-100 text-blue-700'
                                  : letter.status === 'rejected'
                                    ? 'bg-rose-100 text-rose-700'
                                    : 'bg-amber-100 text-amber-700'
                            }`}>
                              {statusLabel}
                    </span>
                  </div>
                    );
                  })()}
                </button>
              ))
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Workflow</h2>
          <div className="rounded-xl bg-gray-50 p-4 border border-gray-200">
            <p className="text-sm font-semibold text-gray-900">1. Draft with rich editor</p>
            <p className="text-sm text-gray-600 mt-1">Use formatting, lists, alignment, tables, and autosave while writing.</p>
          </div>
          <div className="rounded-xl bg-gray-50 p-4 border border-gray-200">
            <p className="text-sm font-semibold text-gray-900">2. Review full letter</p>
            <p className="text-sm text-gray-600 mt-1">Assign the authorized signatory and submit the document for approval.</p>
          </div>
          <div className="rounded-xl bg-gray-50 p-4 border border-gray-200">
            <p className="text-sm font-semibold text-gray-900">3. Export and track</p>
            <p className="text-sm text-gray-600 mt-1">Approved letters carry the saved signature and stay available for export with audit history.</p>
          </div>
          <div className="text-xs text-gray-500 pt-2">
            Last activity: {stats?.last_updated_at ? formatDateTime(stats.last_updated_at) : 'No edits yet'}
          </div>
        </div>
      </div>
    </div>
  );
}